import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  getStoreByOwner, 
  getStoreCustomization, 
  updateStoreCustomization
} from '../lib/firebaseService';
import { Instagram, Facebook, Linkedin, Send, Music } from 'lucide-react';

export default function StoreSocial() {
  const { firebaseUser } = useAuth();
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [formData, setFormData] = useState({
    instagram: '',
    facebook: '',
    tiktok: '',
    telegram: '',
    linkedin: ''
  });

  useEffect(() => {
    if (firebaseUser) {
      loadData();
    }
  }, [firebaseUser]);

  const loadData = async () => {
    try {
      setPageLoading(true);
      const userStore = await getStoreByOwner(firebaseUser.uid);
      if (userStore) {
        setStore(userStore);
        
        const customization = await getStoreCustomization(userStore.id);
        if (customization) {
          setFormData({
            instagram: customization.instagram || '',
            facebook: customization.facebook || '',
            tiktok: customization.tiktok || '',
            telegram: customization.telegram || '',
            linkedin: customization.linkedin || ''
          });
        }
      }
    } catch (error) {
      console.error('Məlumatlar yüklənə bilmədi:', error);
    } finally {
      setPageLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!store) {
      toast.error('Əvvəlcə mağaza yaradın');
      return;
    }

    setLoading(true);
    try {
      await updateStoreCustomization(store.id, formData);
      toast.success('Sosial media linkləri saxlanıldı');
    } catch (error) {
      console.error('Xəta:', error);
      toast.error('Əməliyyat uğursuz oldu');
    } finally {
      setLoading(false);
    }
  };

  if (pageLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">Yüklənir...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (!store) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl">
          <div className="bg-white rounded-xl border border-border p-8 text-center">
            <p className="text-muted-foreground mb-4">Əvvəlcə mağaza yaradın</p>
            <Button onClick={() => window.location.href = '/dashboard/store'}>
              Mağaza Yarat
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-3xl">
        <div className="mb-6">
          <h1 className="text-3xl font-bold">Sosial Media</h1>
          <p className="text-muted-foreground mt-1">Sosial media hesablarınızın linklərini əlavə edin</p>
        </div>

        <div className="bg-white rounded-xl border border-border p-6">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="instagram">
                <div className="flex items-center gap-2 mb-2">
                  <Instagram className="w-5 h-5 text-pink-600" />
                  <span className="font-medium">Instagram</span>
                </div>
              </Label>
              <Input
                id="instagram"
                value={formData.instagram}
                onChange={(e) => setFormData({ ...formData, instagram: e.target.value })}
                placeholder="https://instagram.com/username"
                className="h-11"
              />
            </div>

            <div>
              <Label htmlFor="facebook">
                <div className="flex items-center gap-2 mb-2">
                  <Facebook className="w-5 h-5 text-blue-600" />
                  <span className="font-medium">Facebook</span>
                </div>
              </Label>
              <Input
                id="facebook"
                value={formData.facebook}
                onChange={(e) => setFormData({ ...formData, facebook: e.target.value })}
                placeholder="https://facebook.com/pagename"
                className="h-11"
              />
            </div>

            <div>
              <Label htmlFor="tiktok">
                <div className="flex items-center gap-2 mb-2">
                  <Music className="w-5 h-5 text-gray-900" />
                  <span className="font-medium">TikTok</span>
                </div>
              </Label>
              <Input
                id="tiktok"
                value={formData.tiktok}
                onChange={(e) => setFormData({ ...formData, tiktok: e.target.value })}
                placeholder="https://tiktok.com/@username"
                className="h-11"
              />
            </div>

            <div>
              <Label htmlFor="telegram">
                <div className="flex items-center gap-2 mb-2">
                  <Send className="w-5 h-5 text-blue-500" />
                  <span className="font-medium">Telegram</span>
                </div>
              </Label>
              <Input
                id="telegram"
                value={formData.telegram}
                onChange={(e) => setFormData({ ...formData, telegram: e.target.value })}
                placeholder="https://t.me/username"
                className="h-11"
              />
            </div>

            <div>
              <Label htmlFor="linkedin">
                <div className="flex items-center gap-2 mb-2">
                  <Linkedin className="w-5 h-5 text-blue-700" />
                  <span className="font-medium">LinkedIn</span>
                </div>
              </Label>
              <Input
                id="linkedin"
                value={formData.linkedin}
                onChange={(e) => setFormData({ ...formData, linkedin: e.target.value })}
                placeholder="https://linkedin.com/in/username"
                className="h-11"
              />
            </div>

            <div className="pt-4">
              <Button type="submit" disabled={loading} className="w-full h-11">
                {loading ? 'Saxlanılır...' : 'Yadda Saxla'}
              </Button>
            </div>
          </form>
        </div>

        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>Məsləhət:</strong> Sosial media linklərini düzgün daxil edin. Bu linklər mağazanızın public səhifəsində footer-də görünəcək.
          </p>
        </div>
      </div>
    </DashboardLayout>
  );
}
